import { useTranslation } from 'react-i18next';
import { User, Users, Network } from 'lucide-react';
import { SignalApplicabilityScope, Role } from '@momentum/types';
import { PropertyRow } from '@/shared/components/ui/PropertyRow';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { SectionDivider } from '@/shared/components/ui/SectionDivider';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface ApplicabilityScopeFieldProps {
  value: SignalApplicabilityScope;
  onChange: (value: SignalApplicabilityScope) => void;
}

export const ApplicabilityScopeField = ({
  value,
  onChange,
}: ApplicabilityScopeFieldProps) => {
  const { t } = useTranslation('knowledge');
  const employee = useEmployeeContext();

  const isManager =
    employee.role === Role.manager ||
    employee.role === Role.admin ||
    employee.role === Role.system_admin;

  // Only show for managers — non-managers always default to SELF
  if (!isManager) return null;

  const options = [
    {
      value: SignalApplicabilityScope.SELF,
      label: t('scopeSelf'),
      icon: <User />,
      activeColor: {
        bg: 'bg-blue-600/8',
        text: 'text-blue-800',
        border: 'border-blue-600/25',
        iconColor: 'text-blue-600',
      },
    },
    {
      value: SignalApplicabilityScope.DIRECT_REPORTS,
      label: t('scopeDirectReports'),
      icon: <Users />,
      activeColor: {
        bg: 'bg-purple-600/8',
        text: 'text-purple-800',
        border: 'border-purple-600/25',
        iconColor: 'text-purple-600',
      },
    },
    {
      value: SignalApplicabilityScope.FULL_HIERARCHY,
      label: t('scopeFullHierarchy'),
      icon: <Network />,
      activeColor: {
        bg: 'bg-indigo-600/8',
        text: 'text-indigo-800',
        border: 'border-indigo-600/25',
        iconColor: 'text-indigo-600',
      },
    },
  ];

  return (
    <>
      <SectionDivider
        label={t('applicabilitySection')}
        badge={t('applicabilityBadge')}
      />
      <div className="flex flex-col">
        <PropertyRow
          icon={<Users />}
          label={t('scopeLabel')}
          tooltip={t('scopeTooltip')}
        >
          <ChipToggle
            options={options}
            value={value}
            onChange={(scope) => onChange(scope as SignalApplicabilityScope)}
          />
        </PropertyRow>
      </div>
    </>
  );
};
